import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Button, Form, Row, Col, Table, Badge, Modal, Tab, Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import jsPDF from 'jspdf';

const GestionEmplacements = ({ showModal, handleClose, onLocationsUpdated }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Nouveau formulaire d'emplacement
  const [newLocName, setNewLocName] = useState('');
  const [newLocCategory, setNewLocCategory] = useState('Twin Park');

  // Paramétrage des voitures pour le parc sélectionné
  const [selectedLocId, setSelectedLocId] = useState('');
  const [carRaceList, setCarRaceList] = useState([]);
  const [newCarName, setNewCarName] = useState('');
  const [newCarColor, setNewCarColor] = useState('#3b82f6');
  const [writingNfc, setWritingNfc] = useState(false);

  const handleWriteNfcTag = async (locationId, locationName) => {
    const url = `${window.location.origin}/caisse-nfc/${locationId}`;
    if (!('NDEFReader' in window)) {
      alert("⚠️ L'API Web NFC nécessite Chrome sur un appareil Android avec NFC activé. Si vous utilisez un iPhone ou PC, vous pouvez programmer votre tag NFC via l'application mobile gratuite 'NFC Tools' avec cette URL :\n\n" + url);
      return;
    }
    try {
      setWritingNfc(true);
      const ndef = new window.NDEFReader();
      alert(`📲 Approchez votre tag NFC vierge au dos de votre appareil pour programmer la caisse "${locationName}"...`);
      await ndef.write({
        records: [{ recordType: "url", data: url }]
      });
      alert(`✅ Succès ! Le tag NFC a été écrit avec succès pour la caisse "${locationName}".`);
    } catch (err) {
      console.error("Erreur écriture NFC:", err);
      alert(`Erreur d'écriture NFC : ${err.message || err}`);
    } finally {
      setWritingNfc(false);
    }
  };

  // 🔥 Génération et Téléchargement du PDF à imprimer avec le QR Code de la Caisse
  const downloadLocationQrPdf = async (location) => {
    try {
      const nfcUrl = `${window.location.origin}/caisse-nfc/${location._id}`;
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(nfcUrl)}`;

      const response = await fetch(qrApiUrl);
      const blob = await response.blob();
      const reader = new FileReader();

      reader.onloadend = () => {
        const qrBase64 = reader.result;

        // Créer un PDF au format A6 (105mm x 148mm) pour chevalet / sticker de comptoir
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a6'
        });

        // En-tête Bandeau Sombre
        doc.setFillColor(30, 41, 59);
        doc.rect(0, 0, 105, 28, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(location.name.toUpperCase(), 52.5, 12, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text("POINT DE VENTE & CAISSE EN DIRECT", 52.5, 19, { align: 'center' });

        // Cadre de contour
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.5);
        doc.roundedRect(6, 32, 93, 108, 3, 3);

        doc.setTextColor(15, 23, 42);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.text("FACTURE CLIENT & REÇU DE CAISSE", 52.5, 41, { align: 'center' });

        // Image du QR Code (55mm x 55mm)
        doc.addImage(qrBase64, 'PNG', 25, 45, 55, 55);

        // Encadré d'instructions
        doc.setFillColor(241, 245, 249);
        doc.roundedRect(10, 104, 85, 26, 2, 2, 'F');

        doc.setTextColor(30, 41, 59);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.text("📱 SCANNEZ AVEC VOTRE SMARTPHONE", 52.5, 111, { align: 'center' });

        doc.setTextColor(5, 150, 105);
        doc.setFontSize(8);
        doc.text("📲 OU POSEZ VOTRE TÉLÉPHONE SUR LE BADGE NFC", 52.5, 118, { align: 'center' });

        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.text("Téléchargez votre reçu officiel en PDF instantanément !", 52.5, 125, { align: 'center' });

        // URL Caisse en bas
        doc.setTextColor(148, 163, 184);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5.5);
        doc.text(`URL Caisse: ${nfcUrl}`, 52.5, 136, { align: 'center' });

        // Télécharger le fichier PDF
        doc.save(`QR_Code_Caisse_${location.name.replace(/\s+/g, '_')}.pdf`);
      };

      reader.readAsDataURL(blob);
    } catch (err) {
      console.error("Erreur génération PDF QR Code:", err);
      alert("Erreur lors de la création du PDF QR Code.");
    }
  };

  const user = useSelector((state) => state.AuthReducer.user);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/Locations/getAllLocations').catch(() => ({ data: [] }));
      const data = (res && Array.isArray(res.data)) ? res.data : [];
      setLocations(data);

      const twinLocs = data.filter((l) => !l.category || l.category === 'Twin Park');
      if (twinLocs.length > 0) {
        const targetId = selectedLocId && twinLocs.some(l => l._id === selectedLocId) ? selectedLocId : twinLocs[0]._id;
        setSelectedLocId(targetId);
        const currentLoc = twinLocs.find(l => l._id === targetId);
        setCarRaceList(currentLoc?.carRaceProducts || []);
      }

      if (onLocationsUpdated) onLocationsUpdated(data);
    } catch (err) {
      console.warn('Erreur chargement emplacements:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocId) {
      const found = locations.find((l) => l._id === selectedLocId);
      if (found) {
        setCarRaceList(found.carRaceProducts || []);
      }
    }
  }, [selectedLocId, locations]);

  // Ajouter un emplacement
  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocName.trim()) {
      alert("Veuillez saisir le nom de l'emplacement");
      return;
    }
    try {
      await axios.post('/api/Locations/addLocation', {
        name: newLocName.trim(),
        category: newLocCategory,
      });
      setNewLocName('');
      alert('📍 Emplacement créé avec succès !');
      fetchLocations();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la création de l'emplacement");
    }
  };

  // Supprimer un emplacement
  const handleDeleteLocation = async (id, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'emplacement "${name}" ? (L'historique des rapports passés sera préservé)`)) {
      return;
    }
    try {
      await axios.delete(`/api/Locations/deleteLocation/${id}`);
      alert('🗑️ Emplacement supprimé');
      fetchLocations();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  // Ajouter une voiture au parc sélectionné
  const handleAddCarVariant = () => {
    if (!newCarName.trim()) {
      alert("Veuillez entrer le nom / couleur de la voiture");
      return;
    }
    const updated = [
      ...carRaceList,
      { name: newCarName.trim(), color: newCarColor, active: true }
    ];
    setCarRaceList(updated);
    setNewCarName('');
    saveCarRaceProducts(updated);
  };

  // Supprimer une voiture
  const handleDeleteCarVariant = (index) => {
    const carToDelete = carRaceList[index];
    if (window.confirm(`Supprimer la voiture "${carToDelete?.name}" de cet emplacement ?`)) {
      const updated = carRaceList.filter((_, i) => i !== index);
      setCarRaceList(updated);
      saveCarRaceProducts(updated);
    }
  };

  // Sauvegarder les produits Car Race pour le parc sélectionné
  const saveCarRaceProducts = async (products) => {
    if (!selectedLocId) return;
    try {
      await axios.put(`/api/Locations/updateCarRace/${selectedLocId}`, {
        carRaceProducts: products,
      });
      fetchLocations();
    } catch (err) {
      console.error('Erreur sauvegarde voitures:', err);
      alert('Erreur lors de la mise à jour des voitures');
    }
  };

  const content = (
    <Tab.Container defaultActiveKey="locations">
      <Nav variant="pills" className="mb-3 bg-light p-2 rounded border">
        <Nav.Item>
          <Nav.Link eventKey="locations" className="fw-bold">
            📍 Emplacements (Twin Park & Wellness)
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="carrace" className="fw-bold">
            🏎️ Paramétrage Voitures (Car Race)
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="nfc" className="fw-bold text-success">
            📡 Tags NFC Factures Client
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        {/* ONGLET 1: GESTION DES EMPLACEMENTS */}
        <Tab.Pane eventKey="locations">
          <Card className="mb-4 shadow-sm border-0 bg-light">
            <Card.Body>
              <h6 className="fw-bold text-primary mb-3">➕ Ajouter un Nouvel Emplacement</h6>
              <Form onSubmit={handleAddLocation}>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      placeholder="Nom de l'emplacement (ex: Twin Park - Sousse)"
                      value={newLocName}
                      onChange={(e) => setNewLocName(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={newLocCategory}
                      onChange={(e) => setNewLocCategory(e.target.value)}
                    >
                      <option value="Twin Park">Twin Park</option>
                      <option value="Wellness">Wellness</option>
                      <option value="Spintoss">Spintoss</option>
                      <option value="Autre">Autre</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Button type="submit" variant="primary" className="w-100 fw-bold">
                      Créer
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <h6 className="fw-bold text-secondary mb-2">📋 Liste des Emplacements Actifs</h6>
          <Table responsive striped bordered hover className="align-middle bg-white shadow-sm">
            <thead className="table-primary">
              <tr>
                <th>Nom de l'Emplacement</th>
                <th>Catégorie</th>
                <th>Voitures Car Race</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((loc) => (
                <tr key={loc._id}>
                  <td className="fw-bold text-dark">{loc.name}</td>
                  <td>
                    <Badge bg={loc.category === 'Wellness' ? 'success' : 'primary'}>
                      {loc.category || 'Twin Park'}
                    </Badge>
                  </td>
                  <td>
                    {loc.carRaceProducts && loc.carRaceProducts.length > 0 ? (
                      loc.carRaceProducts.map((car, idx) => (
                        <Badge
                          key={idx}
                          className="me-1"
                          style={{ backgroundColor: car.color || '#3b82f6', color: '#fff' }}
                        >
                          {car.name}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted small">Aucune voiture</span>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteLocation(loc._id, loc.name)}
                    >
                      🗑️ Supprimer
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab.Pane>

        {/* ONGLET 2: PARAMÉTRAGE CAR RACE PAR PARC */}
        <Tab.Pane eventKey="carrace">
          <Card className="mb-4 shadow-sm border-0 bg-light">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold text-primary">📍 Sélectionner le Parc Twin Park</Form.Label>
                <Form.Select
                  value={selectedLocId}
                  onChange={(e) => setSelectedLocId(e.target.value)}
                  className="fw-bold"
                >
                  {locations
                    .filter((l) => !l.category || l.category === 'Twin Park')
                    .map((l) => (
                      <option key={l._id} value={l._id}>
                        {l.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>

              <h6 className="fw-bold text-secondary mt-4 mb-3">➕ Ajouter une Voiture/Produit pour ce Parc</h6>
              <Row className="g-2 mb-3">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Nom ou Couleur (ex: Jaune, Noir, Rouge S, Bleue)"
                    value={newCarName}
                    onChange={(e) => setNewCarName(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <Form.Label className="me-2 mb-0 small">Couleur Badge:</Form.Label>
                    <Form.Control
                      type="color"
                      value={newCarColor}
                      onChange={(e) => setNewCarColor(e.target.value)}
                      title="Choisir la couleur"
                    />
                  </div>
                </Col>
                <Col md={3}>
                  <Button variant="success" className="w-100 fw-bold" onClick={handleAddCarVariant}>
                    ➕ Ajouter Voiture
                  </Button>
                </Col>
              </Row>

              <h6 className="fw-bold text-dark mt-4">🏎️ Voitures Configurées pour ce Parc :</h6>
              {carRaceList.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {carRaceList.map((car, idx) => (
                    <div
                      key={idx}
                      className="d-flex align-items-center p-2 rounded text-white shadow-sm"
                      style={{ backgroundColor: car.color || '#3b82f6' }}
                    >
                      <span className="fw-bold me-2">{car.name}</span>
                      <Button
                        variant="link"
                        className="text-white p-0 ms-2 text-decoration-none fw-bold"
                        onClick={() => handleDeleteCarVariant(idx)}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted italic small mt-2">Aucune voiture configurée pour cet emplacement.</p>
              )}
            </Card.Body>
          </Card>
        </Tab.Pane>

        {/* ONGLET 3: GESTION & PROGRAMMATION DES TAGS NFC PAR CAISSE */}
        <Tab.Pane eventKey="nfc">
          <Card className="mb-4 shadow-sm border-0 bg-light">
            <Card.Body>
              <h5 className="fw-bold text-success mb-2">📡 Tags NFC & QR Code par Caisse</h5>
              <p className="text-muted small mb-4">
                Chaque point de vente dispose d'une URL NFC unique. Collez ou programmez le tag NFC sur le comptoir de la caisse. Lors du paiement, le client scannera ce tag pour récupérer son reçu PDF.
              </p>

              <Table responsive striped bordered hover className="align-middle bg-white shadow-sm">
                <thead className="table-success">
                  <tr>
                    <th>Point de Vente (Caisse)</th>
                    <th>URL NFC Dédiée</th>
                    <th>Aperçu QR Code</th>
                    <th>Actions NFC</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((loc) => {
                    const nfcUrl = `${window.location.origin}/caisse-nfc/${loc._id}`;
                    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(nfcUrl)}`;

                    return (
                      <tr key={loc._id}>
                        <td>
                          <div className="fw-bold text-dark">{loc.name}</div>
                          <Badge bg="secondary" className="mt-1">{loc.category || 'Twin Park'}</Badge>
                        </td>
                        <td>
                          <div className="input-group input-group-sm">
                            <input
                              type="text"
                              readOnly
                              value={nfcUrl}
                              className="form-control font-monospace text-muted"
                              style={{ fontSize: '0.8rem' }}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                navigator.clipboard.writeText(nfcUrl);
                                alert(`Copie réussie !\nURL : ${nfcUrl}`);
                              }}
                            >
                              📋 Copier
                            </button>
                          </div>
                        </td>
                        <td className="text-center">
                          <img
                            src={qrApiUrl}
                            alt={`QR Code NFC ${loc.name}`}
                            width="64"
                            height="64"
                            className="img-thumbnail cursor-pointer"
                            onClick={() => window.open(qrApiUrl, '_blank')}
                            title="Cliquer pour grandir / imprimer"
                          />
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => downloadLocationQrPdf(loc)}
                              className="fw-bold"
                            >
                              📄 Imprimer / PDF QR Code
                            </Button>
                            <Button
                              variant="success"
                              size="sm"
                              disabled={writingNfc}
                              onClick={() => handleWriteNfcTag(loc._id, loc.name)}
                              className="fw-bold"
                            >
                              📲 Écrire sur Tag NFC
                            </Button>
                            <a
                              href={nfcUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary btn-sm text-decoration-none"
                            >
                              👁️ Tester la Caisse
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );

  if (showModal) {
    return (
      <Modal show={showModal} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>⚙️ Paramétrage des Emplacements & Voitures</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white p-4">{content}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <div className="container my-5" style={{ marginTop: '100px' }}>
      <h3 className="fw-bold text-primary mb-4">⚙️ Paramétrage des Emplacements & Voitures</h3>
      {content}
    </div>
  );
};

export default GestionEmplacements;
